import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import * as React from "react";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { toast } from "@/lib/toast";
import { useLocale } from "@/contexts/LocaleContext";
import { SiteShell } from "@/components/SiteShell";

export const Route = createFileRoute("/reset-password")({
  component: ResetPasswordPage,
  head: () => ({ meta: [{ title: "إعادة تعيين كلمة المرور — Lecmo" }] }),
});

function ResetPasswordPage() {
  const { locale, dir } = useLocale();
  const navigate = useNavigate();
  const [recoveryState, setRecoveryState] = React.useState<"checking" | "ready" | "invalid">("checking");
  const readyRef = React.useRef(false);
  const mountedRef = React.useRef(false);
  const [password, setPassword] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  const t = (en: string, ar: string, fr: string) =>
    locale === "ar" ? ar : locale === "fr" ? fr : en;

  // Supabase places the recovery session in URL hash automatically.
  // onAuthStateChange fires with PASSWORD_RECOVERY when ready.
  React.useEffect(() => {
    let active = true;
    let authEventVersion = 0;
    mountedRef.current = true;
    const applyState = (state: "ready" | "invalid") => {
      if (!active) return;
      readyRef.current = state === "ready";
      setRecoveryState(state);
    };
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      if (!active) return;
      if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN" || event === "SIGNED_OUT") {
        authEventVersion += 1;
        applyState(event !== "SIGNED_OUT" && session?.access_token ? "ready" : "invalid");
      }
    });

    // An expired invitation/recovery URL must not silently fall back to an
    // unrelated existing session. Later valid SDK events can still recover.
    const params = [
      new URLSearchParams(window.location.search),
      new URLSearchParams(window.location.hash.slice(1)),
    ];
    const hasLinkError = params.some((p) =>
      ["error", "error_code", "error_description"].some((key) => p.has(key)),
    );
    if (hasLinkError) {
      applyState("invalid");
    } else {
      // Also support reloading an already recovered session. A newer SDK auth
      // event wins over this probe, whether it signs the user in or out.
      const probeVersion = authEventVersion;
      void supabase.auth.getSession().then(({ data: { session }, error }) => {
        if (authEventVersion !== probeVersion) return;
        applyState(!error && session?.access_token ? "ready" : "invalid");
      }).catch(() => {
        if (authEventVersion === probeVersion) applyState("invalid");
      });
    }

    return () => {
      active = false;
      mountedRef.current = false;
      readyRef.current = false;
      data.subscription.unsubscribe();
    };
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mountedRef.current || !readyRef.current || submitting) return;
    if (password.length < 6) {
      toast.error(t("Password must be 6+ chars", "كلمة المرور ٦ أحرف على الأقل", "6 caractères min"));
      return;
    }
    if (password !== confirm) {
      toast.error(t("Passwords do not match", "كلمتا المرور غير متطابقتين", "Les mots de passe ne correspondent pas"));
      return;
    }
    setSubmitting(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      if (!mountedRef.current || !readyRef.current) return;
      toast.success(t("Password updated", "تمّ تحديث كلمة المرور", "Mot de passe mis à jour"));
      navigate({ to: "/account" });
    } catch (err) {
      if (mountedRef.current) toast.error((err as Error).message);
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  };

  return (
    <SiteShell>
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-24" dir={dir}>
      <div className="w-full max-w-md">
        <Link to="/" className="block text-center">
          <span className="inline-flex items-baseline gap-1.5">
            <span className="t-h3 tracking-[var(--tracking-wider)]">LECMO</span>
            <span className="t-eyebrow tracking-[var(--tracking-wider)]">perfume</span>
          </span>
        </Link>
        <h1 className="mt-10 text-center t-h2 text-foreground">
          {t("Reset password", "إعادة تعيين كلمة المرور", "Réinitialiser")}
        </h1>
        <p className="mt-2 text-center t-caption uppercase tracking-[var(--tracking-wider)] text-muted-foreground">
          {t("Choose a new password", "اختر كلمة مرور جديدة", "Choisissez un nouveau mot de passe")}
        </p>

        {recoveryState === "checking" ? (
          <p className="mt-10 text-center t-body-sm text-muted-foreground">
            {t(
              "Verifying your reset link...",
              "جارٍ التحقّق من رابط إعادة التعيين...",
              "Vérification du lien...",
            )}
          </p>
        ) : recoveryState === "invalid" ? (
          <div className="mt-10 space-y-4 text-center" role="alert">
            <p className="t-body-sm text-foreground">
              {t(
                "This link is invalid or expired, or your session could not be verified. Request a new link to continue.",
                "الرابط غير صالح أو انتهت صلاحيته، أو تعذّر التحقّق من جلستك. اطلب رابطاً جديداً للمتابعة.",
                "Ce lien est invalide ou expiré, ou votre session n’a pas pu être vérifiée. Demandez un nouveau lien pour continuer.",
              )}
            </p>
            <Link to="/forgot-password" className="inline-block text-primary underline">
              {t("Request a new link", "طلب رابط جديد", "Demander un nouveau lien")}
            </Link>
            <Link to="/auth" search={{ redirect: "/account", mode: "signin" }} className="block text-primary underline">
              {t("Back to sign in", "العودة لتسجيل الدخول", "Retour à la connexion")}
            </Link>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mt-10 space-y-3">
            <Input
              type="password"
              required
              minLength={6}
              placeholder={t("New password (min 6)", "كلمة المرور الجديدة (٦+)", "Nouveau mot de passe (6+)")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-11"
            />
            <Input
              type="password"
              required
              minLength={6}
              placeholder={t("Confirm password", "تأكيد كلمة المرور", "Confirmer")}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="h-11"
            />
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-primary py-3 t-caption uppercase tracking-[var(--tracking-wider)] text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
            >
              {submitting ? "..." : t("Update password", "تحديث كلمة المرور", "Mettre à jour")}
            </button>
          </form>
        )}
      </div>
    </div>
    </SiteShell>
  );
}
